import type { PriceProposalRow } from '../types/price-proposals.types';

/**
 * Qué le pasa a una propuesta que ya no aplica (spec 275, R1-b): la de una
 * estrategia la recalcula su próxima corrida (o la descarta sola); la de una
 * estrategia pausada, o de un producto que ya no es suyo, no la recalcula su
 * corrida (el servidor manda el motivo en `noLaRecalculaPorque`); la vuelta de
 * una vencida no la recalcula nadie; la del recálculo sin estrategia, sólo
 * otro recálculo.
 */
export type ClaseDeVieja = 'DE_ESTRATEGIA' | 'SIN_CORRIDA' | 'VUELTA' | 'DEL_RECALCULO';

/** El orden fijo en que el aviso de arriba nombra las clases. */
export const ORDEN_DE_LAS_CLASES: readonly ClaseDeVieja[] = [
  'DE_ESTRATEGIA',
  'SIN_CORRIDA',
  'VUELTA',
  'DEL_RECALCULO',
];

export function claseDeVieja(
  f: Pick<PriceProposalRow, 'triggerKind' | 'strategyApplicationId' | 'noLaRecalculaPorque'>
): ClaseDeVieja {
  // El motivo del servidor va primero: sólo viene en una pendiente vieja de una
  // estrategia que su corrida no va a recalcular (nunca en una vuelta).
  if (f.noLaRecalculaPorque) return 'SIN_CORRIDA';
  if (f.triggerKind === 'EXPIRY') return 'VUELTA';
  if (f.triggerKind === 'RECALCULO' && !f.strategyApplicationId) return 'DEL_RECALCULO';
  return 'DE_ESTRATEGIA';
}

/**
 * La frase de la fila abierta, debajo de «Ya no aplica». En `SIN_CORRIDA` la
 * fila muestra `noLaRecalculaPorque` (el motivo exacto); esta frase es el
 * respaldo si no vino.
 */
export const QUE_LE_PASA: Record<ClaseDeVieja, string> = {
  DE_ESTRATEGIA:
    'Su estrategia la recalcula en su próxima corrida, o la descarta sola si ya no corresponde.',
  SIN_CORRIDA: 'Su estrategia no la va a recalcular: ver el motivo.',
  VUELTA: 'Es la vuelta de una estrategia vencida: se deja como está desde la vuelta.',
  DEL_RECALCULO: 'Era del recálculo: recalculá de nuevo en Reglas para actualizarla.',
};

/** Ronda final de R4: la misma frase después de «…desde que se propuso:», en minúscula. */
export function queLePasaEnFrase(
  f: Pick<PriceProposalRow, 'triggerKind' | 'strategyApplicationId' | 'noLaRecalculaPorque'>
): string {
  const t = queLePasa(f);
  return t.charAt(0).toLowerCase() + t.slice(1);
}

/** Lo que dice la fila abierta: el motivo del servidor, o la frase de su clase. */
export function queLePasa(
  f: Pick<PriceProposalRow, 'triggerKind' | 'strategyApplicationId' | 'noLaRecalculaPorque'>
): string {
  return f.noLaRecalculaPorque ?? QUE_LE_PASA[claseDeVieja(f)];
}

const PARTE: Record<ClaseDeVieja, (n: number) => string> = {
  DE_ESTRATEGIA: (n) =>
    n === 1
      ? '1 la recalcula su estrategia en su próxima corrida (o la descarta sola si ya no corresponde)'
      : `${n} las recalcula su estrategia en su próxima corrida (o las descarta sola si ya no corresponden)`,
  SIN_CORRIDA: (n) =>
    n === 1
      ? '1 no la recalcula su estrategia: mirá la fila'
      : `${n} no las recalcula su estrategia: mirá cada fila`,
  VUELTA: (n) =>
    n === 1
      ? '1 es la vuelta de una estrategia vencida: se deja como está desde la vuelta'
      : `${n} son la vuelta de una estrategia vencida: se dejan como están desde la vuelta`,
  DEL_RECALCULO: (n) =>
    n === 1
      ? '1 era del recálculo: recalculá de nuevo en Reglas'
      : `${n} eran del recálculo: recalculá de nuevo en Reglas`,
};

/** El aviso de arriba de Por decidir: cuántas de esta página, y qué pasa con cada clase, en orden fijo. */
export function avisoDeLasViejas(cuantas: Record<ClaseDeVieja, number>): string {
  const partes = ORDEN_DE_LAS_CLASES.filter((k) => cuantas[k] > 0).map((k) => PARTE[k](cuantas[k]));
  return partes.length === 0 ? '' : `No se pueden elegir: ${partes.join('; ')}.`;
}
